import I18n from "i18n-js";
import React, { Component } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { stringify } from "qs";
import { func, shape } from "prop-types";
import SmallBox from "../components/SmallBox";
import {
  ROUTE_ANALYSIS,
  ROUTE_CONTAINERS,
  ROUTE_FIBSDS,
  ROUTE_INSTALLATIONS,
  ROUTE_INTERVENTIONS,
  ROUTE_LEAK_DETECTORS,
  ROUTE_MY_ENTERPRISE,
  ROUTE_PRESSURE_GAUGES,
  ROUTE_PROVIDER_CONTRACTS,
  ROUTE_PROVIDERS,
  ROUTE_SCALES,
  ROUTE_THERMOMETERS,
  ROUTE_USERS,
} from "../../../routes";
import { getDashboardCategoryMetrics } from "../actions/dashboardActions";
import { formatNumber } from "../../common/utils/formatUtils";
import InterventionType from "../../intervention/models/InterventionType";
import TextDateInput from "../../common/components/form/TextDateInput";
import Roles from "../../users/models/Roles";
import ClientTypes from "../../company/models/ClientTypes";
import Spinner from "../../common/components/Spinner";

class Dashboard extends Component {
  static propTypes = {
    metricsByCategory: shape(),
    currentUser: shape(),
    getDashboardCategoryMetrics: func.isRequired,
  };

  static defaultProps = {
    metricsByCategory: undefined,
    currentUser: undefined,
  };

  state = {
    interventionsSinceFilter: new Date(new Date().getFullYear(), 0, 1),
  };

  constructor() {
    super();

    this.onChangeInterventionSinceFilter =
      this.onChangeInterventionSinceFilter.bind(this);
  }

  /**
   * {@inheritdoc}
   */
  componentDidMount() {
    this.fetchBaseCategories();

    if (this.props.currentUser) {
      this.fetchOptionalCategories(this.props.currentUser);
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.currentUser && this.props.currentUser) {
      this.fetchOptionalCategories(this.props.currentUser);
    }
  }

  onChangeInterventionSinceFilter(date) {
    this.setState({ interventionsSinceFilter: date }, () => {
      this.props.getDashboardCategoryMetrics(
        "interventions",
        this.state.interventionsSinceFilter,
      );
    });
  }

  fetchBaseCategories() {
    const { getDashboardCategoryMetrics } = this.props;
    getDashboardCategoryMetrics(
      "interventions",
      this.state.interventionsSinceFilter,
    );
    getDashboardCategoryMetrics("containers");
    getDashboardCategoryMetrics("leak_detectors");
    getDashboardCategoryMetrics("pressure_gauges");
    getDashboardCategoryMetrics("scales");
    getDashboardCategoryMetrics("thermometers");
    getDashboardCategoryMetrics("installations_basic");
    getDashboardCategoryMetrics("installations_after_repair");
    getDashboardCategoryMetrics("installations_periodicity");
  }

  fetchOptionalCategories(currentUser) {
    const { getDashboardCategoryMetrics } = this.props;
    const isTechnician = !currentUser.roles.includes(Roles.MANAGER);
    const canProvideService = [ClientTypes.CF, ClientTypes.DO].includes(
      currentUser.clientType,
    );

    if (currentUser.usesTrackdechets) {
      getDashboardCategoryMetrics("trackdechets");
    }

    if (!isTechnician && canProvideService) {
      getDashboardCategoryMetrics("service_providers");
    }
  }

  getCategoryState(category) {
    const { metricsByCategory } = this.props;
    if (!metricsByCategory || !metricsByCategory[category]) {
      return { data: null, loading: false, error: false };
    }
    return metricsByCategory[category];
  }

  isCategoryLoading(categoryState) {
    return categoryState.loading || (!categoryState.data && !categoryState.error);
  }

  getInstallationsCompositeState() {
    const basicState = this.getCategoryState("installations_basic");
    const afterRepairState = this.getCategoryState("installations_after_repair");
    const periodicityState = this.getCategoryState("installations_periodicity");

    const basic = basicState.data || {};
    const afterRepair = afterRepairState.data || {};
    const periodicity = periodicityState.data || {};

    const data = {
      count: basic.count ?? null,
      toRepair: basic.toRepair ?? null,
      toCheckAfterRepair: afterRepair.toCheckAfterRepair ?? null,
      expectingLeakDetectionSoon: periodicity.expectingLeakDetectionSoon ?? null,
      expectingOilAnalysisSoon: periodicity.expectingOilAnalysisSoon ?? null,
      newAnalysisResultCount: basic.newAnalysisResultCount ?? null,
    };

    return {
      data,
      loading:
        this.isCategoryLoading(basicState) ||
        this.isCategoryLoading(afterRepairState) ||
        this.isCategoryLoading(periodicityState),
      error: basicState.error || afterRepairState.error || periodicityState.error,
      parts: {
        basicState,
        afterRepairState,
        periodicityState,
      },
    };
  }

  getNumbersData() {
    const { currentUser } = this.props;
    const interventionsState = this.getCategoryState("interventions");
    const containersState = this.getCategoryState("containers");
    const leakDetectorsState = this.getCategoryState("leak_detectors");
    const pressureGaugesState = this.getCategoryState("pressure_gauges");
    const scalesState = this.getCategoryState("scales");
    const thermometersState = this.getCategoryState("thermometers");
    const installationsState = this.getInstallationsCompositeState();
    const trackdechetsState = this.getCategoryState("trackdechets");
    const serviceProvidersState = this.getCategoryState("service_providers");
    const interventionsMetrics = interventionsState.data;
    const containersMetrics = containersState.data;
    const leakDetectorMetrics = leakDetectorsState.data;
    const pressureGaugeMetrics = pressureGaugesState.data;
    const scaleMetrics = scalesState.data;
    const thermometerMetrics = thermometersState.data;
    const installationMetrics = installationsState.data;
    const trackdechetsMetrics = trackdechetsState.data;
    const serviceProvidersMetrics = serviceProvidersState.data;
    const interventionsByType = interventionsMetrics
      ? interventionsMetrics.countByType
      : {};
    const interventionsSince = this.state.interventionsSinceFilter.getTime();
    const providerUri = currentUser.serviceProviderContractBeneficiary
      ? ROUTE_PROVIDERS
      : ROUTE_PROVIDER_CONTRACTS;
    const isTechnician = !currentUser.roles.includes(Roles.MANAGER);
    const usesTrackdechets = currentUser.usesTrackdechets;
    const canProvideService = [ClientTypes.CF, ClientTypes.DO].includes(
      currentUser.clientType,
    );
    const valueOrDash = (value) =>
      value === null || value === undefined ? "-" : formatNumber(value);

    const numbers = {
      interventions: {
        legend: (
          <div>
            {I18n.t("pages.dashboard.numbers.interventions.row_title")}{" "}
            {I18n.t("pages.dashboard.numbers.since_filter")}
            <TextDateInput
              className="dashboard__number-legend-datepicker"
              onChange={this.onChangeInterventionSinceFilter}
              defaultValue={this.state.interventionsSinceFilter}
            />
          </div>
        ),
        loading: this.isCategoryLoading(interventionsState),
        data: [
          {
            value: valueOrDash(
              interventionsByType[InterventionType.FILLING],
            ),
            label: I18n.t("pages.dashboard.numbers.interventions.filling"),
            uri: {
              pathname: ROUTE_INTERVENTIONS,
              search: stringify({
                type: InterventionType.FILLING,
                startDate: interventionsSince,
              }),
            },
          },
          {
            value: valueOrDash(interventionsByType[InterventionType.LEAK]),
            label: I18n.t(
              "pages.dashboard.numbers.interventions.leak_detection",
            ),
            uri: {
              pathname: ROUTE_INTERVENTIONS,
              search: stringify({
                type: InterventionType.LEAK,
                startDate: interventionsSince,
              }),
            },
          },
          {
            value: valueOrDash(
              interventionsByType[InterventionType.LEAK_REPAIR],
            ),
            label: I18n.t("pages.dashboard.numbers.interventions.leak_repair"),
            uri: {
              pathname: ROUTE_INTERVENTIONS,
              search: stringify({
                type: InterventionType.LEAK_REPAIR,
                startDate: interventionsSince,
              }),
            },
          },
          {
            value: valueOrDash(
              interventionsByType[InterventionType.DRAINAGE],
            ),
            label: I18n.t("pages.dashboard.numbers.interventions.drainage"),
            uri: {
              pathname: ROUTE_INTERVENTIONS,
              search: stringify({
                type: InterventionType.DRAINAGE,
                startDate: interventionsSince,
              }),
            },
          },
          !isTechnician && {
            value: valueOrDash(
              interventionsMetrics ? interventionsMetrics.toCompleteCount : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.interventions.awaiting_treatment",
            ),
            uri: {
              pathname: ROUTE_FIBSDS,
              search: stringify({
                toComplete: true,
                startDate: interventionsSince,
              }),
            },
            alert:
              interventionsMetrics && interventionsMetrics.toCompleteCount > 0,
          },
          !isTechnician && {
            value: valueOrDash(
              interventionsMetrics
                ? interventionsMetrics.latelyCompletedCount
                : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.interventions.lately_completed",
            ),
            uri: {
              pathname: ROUTE_FIBSDS,
              search: stringify({
                latelyCompleted: true,
                startDate: interventionsSince,
              }),
            },
          },
        ].filter((v) => v),
      },
      containers: {
        break: true,
        loading: this.isCategoryLoading(containersState),
        data: [
          {
            value: valueOrDash(containersMetrics ? containersMetrics.count : null),
            label: I18n.t("pages.dashboard.numbers.containers.known"),
            uri: ROUTE_CONTAINERS,
          },
          {
            value: valueOrDash(
              containersMetrics ? containersMetrics.stressTestAlertCount : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.containers.needs_stress_test",
            ),
            uri: {
              pathname: ROUTE_CONTAINERS,
              search: stringify({ needsStressTest: true }),
            },
            alert:
              containersMetrics && containersMetrics.stressTestAlertCount > 0,
          },
          {
            value: valueOrDash(
              containersMetrics ? containersMetrics.overloaded : null,
            ),
            label: I18n.t("pages.dashboard.numbers.containers.overloaded"),
            uri: {
              pathname: ROUTE_CONTAINERS,
              search: stringify({ overloaded: true }),
            },
            alert: containersMetrics && containersMetrics.overloaded > 0,
          },
        ],
      },
      leak_detectors: {
        loading: this.isCategoryLoading(leakDetectorsState),
        data: [
          {
            value: valueOrDash(
              leakDetectorMetrics ? leakDetectorMetrics.count : null,
            ),
            label: I18n.t("pages.dashboard.numbers.leak_detectors.known"),
            uri: ROUTE_LEAK_DETECTORS,
          },
          {
            value: valueOrDash(
              leakDetectorMetrics ? leakDetectorMetrics.toCheckSoon : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.leak_detectors.to_check_soon",
            ),
            uri: {
              pathname: ROUTE_LEAK_DETECTORS,
              search: stringify({
                endDate: moment().subtract(10, "months").toDate().getTime(),
                }),
            },
            alert: leakDetectorMetrics && leakDetectorMetrics.toCheckSoon > 0,
          },
        ],
      },
      pressure_gauges: {
        loading: this.isCategoryLoading(pressureGaugesState),
        data: [
          {
            value: valueOrDash(
              pressureGaugeMetrics ? pressureGaugeMetrics.count : null,
            ),
            label: I18n.t("pages.dashboard.numbers.pressure_gauges.known"),
            uri: ROUTE_PRESSURE_GAUGES,
          },
          {
            value: valueOrDash(
              pressureGaugeMetrics ? pressureGaugeMetrics.toCheckSoon : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.pressure_gauges.to_check_soon",
            ),
            uri: {
              pathname: ROUTE_PRESSURE_GAUGES,
              search: stringify({
                endDate: moment().subtract(10, "months").toDate().getTime(),
                }),
            },
            alert:
              pressureGaugeMetrics && pressureGaugeMetrics.toCheckSoon > 0,
          },
        ],
      },
      scales: {
        loading: this.isCategoryLoading(scalesState),
        data: [
          {
            value: valueOrDash(scaleMetrics ? scaleMetrics.count : null),
            label: I18n.t("pages.dashboard.numbers.scales.known"),
            uri: ROUTE_SCALES,
          },
          {
            value: valueOrDash(scaleMetrics ? scaleMetrics.toCheckSoon : null),
            label: I18n.t(
              "pages.dashboard.numbers.scales.to_check_soon",
            ),
            uri: {
              pathname: ROUTE_SCALES,
              search: stringify({
                endDate: moment().subtract(10, "months").toDate().getTime(),
                }),
            },
            alert: scaleMetrics && scaleMetrics.toCheckSoon > 0,
          },
        ],
      },
      thermometers: {
        loading: this.isCategoryLoading(thermometersState),
        data: [
          {
            value: valueOrDash(
              thermometerMetrics ? thermometerMetrics.count : null,
            ),
            label: I18n.t("pages.dashboard.numbers.thermometers.known"),
            uri: ROUTE_THERMOMETERS,
          },
          {
            value: valueOrDash(
              thermometerMetrics ? thermometerMetrics.toCheckSoon : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.thermometers.to_check_soon",
            ),
            uri: {
              pathname: ROUTE_THERMOMETERS,
              search: stringify({
                endDate: moment().subtract(10, "months").toDate().getTime(),
                }),
            },
            alert: thermometerMetrics && thermometerMetrics.toCheckSoon > 0,
          },
        ],
      },
      installations: {
        loading: this.isCategoryLoading(installationsState),
        data: [
          {
            value: valueOrDash(
              installationMetrics ? installationMetrics.count : null,
            ),
            label: I18n.t("pages.dashboard.numbers.installations.known"),
            uri: {
              pathname: ROUTE_INSTALLATIONS,
              search: stringify({ active: true }),
            },
          },
          {
            value: valueOrDash(
              installationMetrics ? installationMetrics.toRepair : null,
            ),
            label: I18n.t("pages.dashboard.numbers.installations.to_repair"),
            uri: {
              pathname: ROUTE_INSTALLATIONS,
              search: stringify({ hasLeaks: true }),
            },
            alert: installationMetrics && installationMetrics.toRepair > 0,
          },
          {
            value: valueOrDash(
              installationMetrics
                ? installationMetrics.toCheckAfterRepair
                : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.installations.to_check_after_repair",
            ),
            uri: {
              pathname: ROUTE_INSTALLATIONS,
              search: stringify({ mustBeCheckedAfterRepair: true }),
            },
            alert:
              installationMetrics && installationMetrics.toCheckAfterRepair > 0,
          },
          {
            value: valueOrDash(
              installationMetrics
                ? installationMetrics.expectingLeakDetectionSoon
                : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.installations.expecting_leak_detection",
            ),
            uri: {
              pathname: ROUTE_INSTALLATIONS,
              search: stringify({ expectLeakDetectionSoon: true }),
            },
            alert:
              installationMetrics &&
              installationMetrics.expectingLeakDetectionSoon > 0,
          },
          {
            value: valueOrDash(
              installationMetrics
                ? installationMetrics.expectingOilAnalysisSoon
                : null,
            ),
            label: I18n.t("pages.dashboard.numbers.installations.expecting_oil_analysis"),
            uri: {
              pathname: ROUTE_INSTALLATIONS,
              search: stringify({ expectOilAnalysisSoon: true }),
            },
            alert:
              installationMetrics &&
              installationMetrics.expectingOilAnalysisSoon > 0,
          },
          {
            value: valueOrDash(
              installationMetrics
                ? installationMetrics.newAnalysisResultCount
                : null,
            ),
            label: I18n.t("pages.dashboard.numbers.installations.new_analysis_result"),
            uri: {
              pathname: ROUTE_ANALYSIS,
              search: stringify({ hasResultToView: true }),
            },
            alert:
              installationMetrics &&
              installationMetrics.newAnalysisResultCount > 0,
          },
        ],
      },
    };

    if (usesTrackdechets) {
      numbers.trackdechets = {
        break: true,
        loading: this.isCategoryLoading(trackdechetsState),
        data: [
          {
            value: valueOrDash(
              trackdechetsMetrics
                ? trackdechetsMetrics.companiesWithAnomalies
                : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.trackdechets.companies_with_anomalies",
            ),
            uri: ROUTE_MY_ENTERPRISE,
            alert:
              trackdechetsMetrics &&
              trackdechetsMetrics.companiesWithAnomalies > 0,
          },
          {
            value: valueOrDash(
              trackdechetsMetrics
                ? trackdechetsMetrics.usersNotAssociated
                : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.trackdechets.users_not_associated",
            ),
            uri: {
              pathname: ROUTE_USERS,
              search: stringify({ isAssociatedToTrackdechets: true }),
            },
            alert:
              trackdechetsMetrics && trackdechetsMetrics.usersNotAssociated > 0,
          },
          {
            value: valueOrDash(
              trackdechetsMetrics
                ? trackdechetsMetrics.interventionsWithErrors
                : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.trackdechets.interventions_with_errors",
            ),
            uri: {
              pathname: ROUTE_INTERVENTIONS,
              search: stringify({ trackdechetsError: true }),
            },
            alert:
              trackdechetsMetrics &&
              trackdechetsMetrics.interventionsWithErrors > 0,
          },
        ],
      };
    }

    if (!isTechnician && canProvideService) {
      numbers.service_providers = {
        break: true,
        loading: this.isCategoryLoading(serviceProvidersState),
        data: [
          {
            value: valueOrDash(
              serviceProvidersMetrics ? serviceProvidersMetrics.active : null,
            ),
            label: I18n.t("pages.dashboard.numbers.service_providers.active"),
            uri: {
              pathname: providerUri,
              search: stringify({ active: true }),
            },
          },
          {
            value: valueOrDash(
              serviceProvidersMetrics
                ? serviceProvidersMetrics.expiringSoon
                : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.service_providers.to_renew_soon",
            ),
            uri: {
              pathname: providerUri,
              search: stringify({ toRenew: true }),
            },
            alert:
              serviceProvidersMetrics && serviceProvidersMetrics.expiringSoon > 0,
          },
          {
            value: valueOrDash(
              serviceProvidersMetrics
                ? serviceProvidersMetrics.pendingApproval
                : null,
            ),
            label: I18n.t(
              "pages.dashboard.numbers.service_providers.waiting_approval",
            ),
            uri: {
              pathname: providerUri,
              search: stringify({ approve: true }),
            },
            alert:
              serviceProvidersMetrics &&
              serviceProvidersMetrics.pendingApproval > 0,
          },
        ],
      };
    }

    return numbers;
  }

  renderNumber(value, label, uri, alert) {
    return (
      <div className="dashboard__number" key={label}>
        <SmallBox value={value} label={label} moreUri={uri} alert={alert} />
      </div>
    );
  }

  renderNumbersRow(row, data, legend, loading) {
    const numbers = data.map((d) =>
      this.renderNumber(d.value, d.label, d.uri, d.alert || false),
    );

    return (
      <fieldset className="dashboard__number-group" key={row}>
        <legend className="float-none px-2">
          <div className="dashboard__legend-content">
            {legend || I18n.t(`pages.dashboard.numbers.${row}.row_title`)}
          </div>
          <Spinner
            show={loading}
            className="dashboard__legend-spinner"
            aria-hidden="true"
          />
        </legend>
        <div className="dashboard__number-row">{numbers}</div>
      </fieldset>
    );
  }

  renderNumbersSection(data) {
    const rows = [];

    Object.keys(data).forEach((key) => {
      if (data[key].break) {
        rows.push(<div className="flex-break" key={`break-before-${key}`} />);
      }

      rows.push(
        this.renderNumbersRow(
          key,
          data[key].data,
          data[key].legend,
          data[key].loading,
        ),
      );
    });

    return <section className="dashboard__numbers">{rows}</section>;
  }

  renderAlertText(alertKey, count) {
    const transNs = "pages.dashboard.alert_summary.alerts";
    return I18n.t(`${transNs}.${alertKey}`, { count });
  }

  renderAlertSummary() {
    const { currentUser } = this.props;
    if (!currentUser) {
      return null;
    }

    const isTechnician = !currentUser.roles.includes(Roles.MANAGER);
    const canProvideService = [ClientTypes.CF, ClientTypes.DO].includes(
      currentUser.clientType,
    );
    const interventionsState = this.getCategoryState("interventions");
    const containersState = this.getCategoryState("containers");
    const leakDetectorsState = this.getCategoryState("leak_detectors");
    const pressureGaugesState = this.getCategoryState("pressure_gauges");
    const scalesState = this.getCategoryState("scales");
    const thermometersState = this.getCategoryState("thermometers");
    const installationsState = this.getInstallationsCompositeState();
    const installationsBasicState = installationsState.parts.basicState;
    const installationsAfterRepairState = installationsState.parts.afterRepairState;
    const installationsPeriodicityState =
      installationsState.parts.periodicityState;
    const trackdechetsState = this.getCategoryState("trackdechets");
    const serviceProvidersState = this.getCategoryState("service_providers");

    const interventionsMetrics = interventionsState.data;
    const containersMetrics = containersState.data;
    const leakDetectorMetrics = leakDetectorsState.data;
    const pressureGaugeMetrics = pressureGaugesState.data;
    const scaleMetrics = scalesState.data;
    const thermometerMetrics = thermometersState.data;
    const installationMetrics = installationsState.data;
    const trackdechetsMetrics = trackdechetsState.data;
    const serviceProvidersMetrics = serviceProvidersState.data;

    const lines = [
      {
        key: "interventions:to_complete",
        state: interventionsState,
        count: interventionsMetrics ? interventionsMetrics.toCompleteCount : 0,
        show: !isTechnician,
      },
      {
        key: "containers:overloaded",
        state: containersState,
        count: containersMetrics ? containersMetrics.overloaded : 0,
        show: true,
      },
      {
        key: "containers:needs_stress_test",
        state: containersState,
        count: containersMetrics ? containersMetrics.stressTestAlertCount : 0,
        show: true,
      },
      {
        key: "leak_detectors:to_check_soon",
        state: leakDetectorsState,
        count: leakDetectorMetrics ? leakDetectorMetrics.toCheckSoon : 0,
        show: true,
      },
      {
        key: "pressure_gauges:to_check_soon",
        state: pressureGaugesState,
        count: pressureGaugeMetrics ? pressureGaugeMetrics.toCheckSoon : 0,
        show: true,
      },
      {
        key: "scales:to_check_soon",
        state: scalesState,
        count: scaleMetrics ? scaleMetrics.toCheckSoon : 0,
        show: true,
      },
      {
        key: "thermometers:to_check_soon",
        state: thermometersState,
        count: thermometerMetrics ? thermometerMetrics.toCheckSoon : 0,
        show: true,
      },
      {
        key: "installations:to_repair",
        state: installationsBasicState,
        count: installationMetrics ? installationMetrics.toRepair : 0,
        show: true,
      },
      {
        key: "installations:to_check_after_repair",
        state: installationsAfterRepairState,
        count: installationMetrics ? installationMetrics.toCheckAfterRepair : 0,
        show: true,
      },
      {
        key: "installations:expecting_leak_detection",
        state: installationsPeriodicityState,
        count: installationMetrics
          ? installationMetrics.expectingLeakDetectionSoon
          : 0,
        show: true,
      },
      {
        key: "installations:expecting_oil_analysis",
        state: installationsPeriodicityState,
        count: installationMetrics
          ? installationMetrics.expectingOilAnalysisSoon
          : 0,
        show: true,
      },
      {
        key: "installations:new_analysis_result",
        state: installationsBasicState,
        count: installationMetrics ? installationMetrics.newAnalysisResultCount : 0,
        show: true,
      },
      {
        key: "trackdechets:companies_with_anomalies",
        state: trackdechetsState,
        count: trackdechetsMetrics ? trackdechetsMetrics.companiesWithAnomalies : 0,
        show: currentUser.usesTrackdechets,
      },
      {
        key: "trackdechets:users_not_associated",
        state: trackdechetsState,
        count: trackdechetsMetrics ? trackdechetsMetrics.usersNotAssociated : 0,
        show: currentUser.usesTrackdechets,
      },
      {
        key: "trackdechets:interventions_with_errors",
        state: trackdechetsState,
        count: trackdechetsMetrics ? trackdechetsMetrics.interventionsWithErrors : 0,
        show: currentUser.usesTrackdechets,
      },
      {
        key: "service_providers:expiring_soon",
        state: serviceProvidersState,
        count: serviceProvidersMetrics ? serviceProvidersMetrics.expiringSoon : 0,
        show: !isTechnician && canProvideService,
      },
      {
        key: "service_providers:waiting_approval",
        state: serviceProvidersState,
        count: serviceProvidersMetrics
          ? serviceProvidersMetrics.pendingApproval
          : 0,
        show: !isTechnician && canProvideService,
      },
    ];

    const eligibleLines = lines.filter((line) => line.show);
    const alerts = eligibleLines
      .map((line, index) => {
        const loading = this.isCategoryLoading(line.state);
        if (loading || line.count <= 0) {
          return null;
        }
        const content = this.renderAlertText(line.key, line.count);
        return <li key={`${line.key}-${index}`}>{content}</li>;
      })
      .filter(Boolean);
    const hasLoading = eligibleLines.some((line) =>
      this.isCategoryLoading(line.state),
    );

    return (
      <section className="col dashboard__alert_summary">
        <div className="alert alert-hint">
          <h5 className="alert-heading">
            <span className="dashboard__alert-title">
              {I18n.t("pages.dashboard.alert_summary.title")}
            </span>
            <Spinner
              show={hasLoading}
              className="dashboard__alert-title-spinner"
              aria-hidden="true"
            />
          </h5>
          {alerts.length > 0 && <ul>{alerts}</ul>}
        </div>
      </section>
    );
  }

  /**
   * {@inheritdoc}
   */
  render() {
    const { currentUser } = this.props;

    return (
      <div className="dashboard">
        <h1>{I18n.t("pages.dashboard.title")}</h1>
        {currentUser === undefined && <div className="loader loader--big" />}
        {currentUser === null && <p>{I18n.t("common.loadingApiError")}</p>}
        {currentUser && this.renderAlertSummary()}
        {currentUser && this.renderNumbersSection(this.getNumbersData())}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  metricsByCategory: state.dashboard.metricsByCategory,
  currentUser: state.auth.userProfile,
});

const mapDispatchToProps = (dispatch) => ({
  getDashboardCategoryMetrics: (category, date) =>
    dispatch(getDashboardCategoryMetrics(category, date)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
